/**
 * Section « Mon compte » dans Settings — Phase 4, incrément 4.3.
 *
 * Vit dans `modules/settings/` car c'est dans la vue Settings que
 * la section est rendue (l'utilisateur va dans « Foyer » / « Réglages »
 * → « Mon compte »). On le sépare en module à part pour ne pas
 * polluer `View.ts` qui est déjà gros.
 *
 * États :
 *
 *   - `loading` : skeleton minimal pendant que `initSession` résout.
 *   - `anonymous` (non offline) : empty state avec 2 boutons
 *     « Se connecter » et « Créer un compte ».
 *   - `anonymous + offline` : même empty state mais message qui
 *     explique que le service est down (sans culpabiliser — §2.1).
 *   - `authenticated` : carte avec nom + email + bouton « Se
 *     déconnecter ».
 *
 * Les modaux login/register utilisent `ui/Modal.ts`. Validation
 * inline côté UI (avant l'appel API) pour donner du feedback rapide.
 * Les erreurs serveur (401 / 409) sont mappées vers des messages
 * `t()` côté UI — l'API renvoie des codes neutres.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './View.module.css';
import { getLang, setLang, SUPPORTED_LANGS, t } from '../../i18n/index.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import {
  getSession,
  loginAndUpdateSession,
  logoutAndUpdateSession,
  registerAndUpdateSession,
} from '../../stores/session.js';
import type { AuthLang, AuthUser } from '../../api/auth.js';
import { ApiError } from '../../api/client.js';
import { modalTemplate } from '../../ui/Modal.js';
import { navigate } from '../../lib/router.js';

const MODAL_LOGIN = 'settings:login';
const MODAL_REGISTER = 'settings:register';

/**
 * État local des formulaires — préservé entre re-renders du shell
 * pour ne pas perdre la saisie quand l'utilisateur ouvre/ferme la
 * modal ou switch de langue.
 */
interface FormState {
  email: string;
  password: string;
  displayName: string;
  /** Code d'erreur i18n à afficher (key dans `account_err_*`). */
  error: string | null;
  /** `true` pendant la requête réseau — désactive le bouton submit. */
  submitting: boolean;
}

const loginForm: FormState = {
  email: '',
  password: '',
  displayName: '',
  error: null,
  submitting: false,
};

const registerForm: FormState = {
  email: '',
  password: '',
  displayName: '',
  error: null,
  submitting: false,
};

function resetForm(form: FormState): void {
  form.email = '';
  form.password = '';
  form.displayName = '';
  form.error = null;
  form.submitting = false;
}

/**
 * Mappe un code d'erreur retourné par l'API (ou un état réseau)
 * vers une clé i18n bienveillante (§2.1).
 */
function errorMessageKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'invalid_credentials') return 'account_err_invalid_credentials';
    if (err.code === 'email_taken') return 'account_err_email_taken';
    if (err.status === 503) return 'account_err_network';
  }
  // TypeError réseau ou ApiError non-codée — fallback générique.
  if (err instanceof TypeError) return 'account_err_network';
  return 'account_err_generic';
}

/**
 * Validation côté client — feedback immédiat sans aller-retour API.
 * Reproduit (en plus simple) la validation Zod côté serveur.
 */
function validateLogin(form: FormState): string | null {
  if (!form.email.includes('@')) return 'account_err_invalid_email';
  if (form.password.length < 1) return 'account_err_password_too_short';
  return null;
}

function validateRegister(form: FormState): string | null {
  if (!form.email.includes('@')) return 'account_err_invalid_email';
  if (form.password.length < 8) return 'account_err_password_too_short';
  if (!form.displayName.trim()) return 'account_err_name_required';
  return null;
}

// ─── Sub-templates ──────────────────────────────────────────────────

function authUserCard(user: AuthUser): TemplateResult {
  const onLogout = async (): Promise<void> => {
    await logoutAndUpdateSession();
    // Feedback alpha : après déconnexion, retour à la page de
    // connexion (les données locales restent — §2.4).
    navigate('/login');
  };
  return html`<div class=${styles.accountCard} data-testid="account-card-authenticated">
    <div class=${styles.accountCardHead}>
      <div>
        <div class=${styles.accountKicker}>${t('account_signed_in_as')}</div>
        <div class=${styles.accountName}>${user.displayName}</div>
        <div class=${styles.accountEmail}>${user.email}</div>
      </div>
    </div>
    <button
      type="button"
      class=${styles.btnGhostDanger}
      data-testid="account-logout-btn"
      @click=${(): void => {
        void onLogout();
      }}
    >
      ${t('account_btn_logout')}
    </button>
  </div>`;
}

function anonymousCard(offline: boolean): TemplateResult {
  return html`<div class=${styles.accountCard} data-testid="account-card-anonymous">
    <div class=${styles.accountKicker}>${t('account_anonymous_title')}</div>
    <p class=${styles.accountSub}>${t('account_anonymous_sub')}</p>
    ${offline
      ? html`<p class=${styles.accountOffline} data-testid="account-offline">
          ${t('account_offline')}
        </p>`
      : null}
    <div class=${styles.accountActions}>
      <button
        type="button"
        class=${styles.btnSecondary}
        data-testid="account-login-btn"
        @click=${(): void => {
          resetForm(loginForm);
          openModal(MODAL_LOGIN);
        }}
      >
        ${t('account_btn_login')}
      </button>
      <button
        type="button"
        class=${styles.btnPrimary}
        data-testid="account-register-btn"
        @click=${(): void => {
          resetForm(registerForm);
          openModal(MODAL_REGISTER);
        }}
      >
        ${t('account_btn_register')}
      </button>
    </div>
  </div>`;
}

function loadingCard(): TemplateResult {
  return html`<div class=${styles.accountCard} data-testid="account-card-loading">
    <div class=${styles.accountKicker}>${t('account_section')}</div>
    <div class=${styles.accountSkeleton}></div>
  </div>`;
}

// ─── Modals (login + register) ─────────────────────────────────────

function loginFormTemplate(rerender: () => void): TemplateResult {
  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (loginForm.submitting) return;
    const validationError = validateLogin(loginForm);
    if (validationError) {
      loginForm.error = validationError;
      rerender();
      return;
    }
    loginForm.error = null;
    loginForm.submitting = true;
    rerender();
    try {
      await loginAndUpdateSession({
        email: loginForm.email.trim(),
        password: loginForm.password,
      });
      resetForm(loginForm);
      closeModal();
    } catch (err) {
      loginForm.error = errorMessageKey(err);
      loginForm.submitting = false;
      rerender();
    }
  };

  return html`<form class=${styles.form} @submit=${onSubmit} data-testid="login-form">
    <div class=${styles.field}>
      <label for="login-email">${t('account_email_label')}</label>
      <input
        id="login-email"
        type="email"
        autocomplete="email"
        data-testid="login-email"
        .value=${loginForm.email}
        @input=${(e: Event): void => {
          loginForm.email = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <div class=${styles.field}>
      <label for="login-password">${t('account_password_label')}</label>
      <input
        id="login-password"
        type="password"
        autocomplete="current-password"
        data-testid="login-password"
        .value=${loginForm.password}
        @input=${(e: Event): void => {
          loginForm.password = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    ${loginForm.error
      ? html`<div class=${styles.formError} data-testid="login-error" role="alert">
          ${t(loginForm.error as Parameters<typeof t>[0])}
        </div>`
      : null}
    <div class=${styles.formActions}>
      <button
        type="button"
        class=${styles.cancel}
        @click=${(): void => {
          closeModal();
        }}
      >
        ${t('cancel')}
      </button>
      <button
        type="submit"
        class=${styles.submit}
        ?disabled=${loginForm.submitting}
        data-testid="login-submit"
      >
        ${t('account_btn_submit_login')}
      </button>
    </div>
  </form>`;
}

function registerFormTemplate(rerender: () => void): TemplateResult {
  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    if (registerForm.submitting) return;
    const validationError = validateRegister(registerForm);
    if (validationError) {
      registerForm.error = validationError;
      rerender();
      return;
    }
    registerForm.error = null;
    registerForm.submitting = true;
    rerender();
    try {
      await registerAndUpdateSession({
        email: registerForm.email.trim(),
        password: registerForm.password,
        displayName: registerForm.displayName.trim(),
        lang: getLang() as AuthLang,
      });
      resetForm(registerForm);
      closeModal();
    } catch (err) {
      registerForm.error = errorMessageKey(err);
      registerForm.submitting = false;
      rerender();
    }
  };
  return html`<form class=${styles.form} @submit=${onSubmit} data-testid="register-form">
    <div class=${styles.field}>
      <label for="reg-name">${t('account_name_label')}</label>
      <input
        id="reg-name"
        type="text"
        autocomplete="given-name"
        data-testid="register-name"
        .value=${registerForm.displayName}
        @input=${(e: Event): void => {
          registerForm.displayName = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <div class=${styles.field}>
      <label for="reg-email">${t('account_email_label')}</label>
      <input
        id="reg-email"
        type="email"
        autocomplete="email"
        data-testid="register-email"
        .value=${registerForm.email}
        @input=${(e: Event): void => {
          registerForm.email = (e.target as HTMLInputElement).value;
        }}
      />
    </div>
    <div class=${styles.field}>
      <label for="reg-password">${t('account_password_label')}</label>
      <input
        id="reg-password"
        type="password"
        autocomplete="new-password"
        minlength="8"
        data-testid="register-password"
        .value=${registerForm.password}
        @input=${(e: Event): void => {
          registerForm.password = (e.target as HTMLInputElement).value;
        }}
      />
      <small class=${styles.fieldHint}>${t('account_password_hint')}</small>
    </div>
    ${registerForm.error
      ? html`<div class=${styles.formError} data-testid="register-error" role="alert">
          ${t(registerForm.error as Parameters<typeof t>[0])}
        </div>`
      : null}
    <div class=${styles.formActions}>
      <button
        type="button"
        class=${styles.cancel}
        @click=${(): void => {
          closeModal();
        }}
      >
        ${t('cancel')}
      </button>
      <button
        type="submit"
        class=${styles.submit}
        ?disabled=${registerForm.submitting}
        data-testid="register-submit"
      >
        ${t('account_btn_submit_register')}
      </button>
    </div>
  </form>`;
}

// ─── Exported templates ─────────────────────────────────────────────

/**
 * Section principale rendue dans Settings/View.ts. Auto-câblée à
 * `getSession()` qui doit avoir été initialisée via `initSession()`
 * (fait dans main.ts au boot).
 *
 * `rerender` est passé par l'appelant — typiquement `bumpUi()` du
 * store ui pour forcer un cycle après une mutation locale (changement
 * d'input dans un form, par exemple).
 */
export function accountSectionTemplate(rerender: () => void): TemplateResult {
  void rerender; // Le store session déclenche déjà un re-render via subscribe — pas besoin ici.
  void SUPPORTED_LANGS; // Réservé pour l'extension Phase 4.4+ (lang picker dans le register).
  void setLang;
  const session = getSession();
  let card: TemplateResult;
  if (session.status === 'loading') {
    card = loadingCard();
  } else if (session.status === 'authenticated' && session.user) {
    card = authUserCard(session.user);
  } else {
    card = anonymousCard(session.offline);
  }
  return html`<section class=${styles.section} data-testid="account-section">
    <h2 class=${styles.sectionTitle}>${t('account_section')}</h2>
    ${card}
  </section>`;
}

/**
 * Modale login — à rendre depuis `View.ts` quand `getModal() ===
 * MODAL_LOGIN`. Exporté pour que `View.ts` puisse câbler le modal
 * au niveau racine de la section.
 */
export function loginModalTemplate(rerender: () => void): TemplateResult | null {
  if (getModal() !== MODAL_LOGIN) return null;
  return modalTemplate({
    title: t('account_modal_login'),
    content: loginFormTemplate(rerender),
    onClose: (): void => {
      closeModal();
    },
    closeLabel: t('close'),
    testid: 'modal-login',
  });
}

export function registerModalTemplate(rerender: () => void): TemplateResult | null {
  if (getModal() !== MODAL_REGISTER) return null;
  return modalTemplate({
    title: t('account_modal_register'),
    content: registerFormTemplate(rerender),
    onClose: (): void => {
      closeModal();
    },
    closeLabel: t('close'),
    testid: 'modal-register',
  });
}
